/**
 * The app shell.
 *
 * Six sections, and the ordering is the point: Home is what you must do, Keepers and Draft are
 * the two things the league actually argues about, League is what the rules say, History is the
 * record, Settings is the machinery. A manager who never clicks past Home should still know
 * whether he owes anybody anything.
 *
 * Two levels of navigation rather than one long tab strip. Twenty-odd destinations in a row is a
 * wall; six with a contextual second row is a map. The second row only appears when a section has
 * more than one view.
 *
 * Role filtering happens at both levels, and a section with nothing visible in it disappears
 * entirely — a manager should never be shown a door he cannot open.
 *
 * Views are plain state rather than a router. Swapping in react-router later changes this file
 * and nothing else.
 */

import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { managerShort } from '../domain/identity.js';
import { Home } from './Home.js';
import { League } from './League.js';
import { Rules } from './Rules.js';
import { Proposals } from './Proposals.js';
import { HistoryHub } from './HistoryHub.js';
import { KeeperCalculator } from './KeeperCalculator.js';
import { HistoryKeepers } from './HistoryKeepers.js';
import { FarmLog } from './FarmLog.js';
import { DeclareKeepers } from './DeclareKeepers.js';
import { DraftPosition } from './DraftPosition.js';
import { DraftBoard } from './DraftBoard.js';
import { MockDraft } from './MockDraft.js';
import { DraftHistory, type DraftFocus } from './DraftHistory.js';
import { AdminAudit } from './AdminAudit.js';
import { useActingAsAudit, useSignInAudit, useViewAudit } from './auditor.js';
import { AdminSettings } from './AdminSettings.js';
import { CommissionerTools } from './CommissionerTools.js';
import { Indexes } from './Indexes.js';
import { DraftTools } from './DraftTools.js';
import { SavedData } from './SavedData.js';
import { usePairingLoader, useRosterLoader, useRosterVersion } from './pairings.js';
import { useChrome } from './chrome.js';
import { NavIcon, type NavIconName } from './NavIcons.js';
import { DraftStrip } from './DraftStrip.js';
import { CommitteeStrip } from './CommitteeStrip.js';
import { LotteryStrip } from './LotteryStrip.js';
import { TouchHints } from './TouchHints.js';
import { PullToRefresh } from './PullToRefresh.js';
import { LOGO_URL, FAVICON_URL } from './logo.js';
import { formatHash, parseHash, sameWhere } from './route.js';
import { seasonWeek } from '../domain/phase.js';
import { PhaseFlow, Countdown } from './PhaseFlow.js';
import { SignIn } from './SignIn.js';
import { AddToHome } from './AddToHome.js';
import { RuleProvider, setRuleNavigator } from './RuleText.js';
import { MoreSheet } from './MoreSheet.js';
import { IdentityPanel } from './IdentityPanel.js';
import { ProfileMenu } from './ProfileMenu.js';
import { AccessRequests } from './AccessRequests.js';
import { useAccessRequests } from './useAccessRequests.js';
import { AvatarProvider } from './avatars.js';
import { GmBadge } from './GmBadge.js';
import { GMS, ROLE_LABEL, SessionProvider, useSession } from '../store/session.js';
import { atLeast, type Role } from '../store/types.js';
import { LeagueStoreProvider, useLeagueStore } from '../store/provider.js';
import './styles.css';
import { appBadgeCount, showAppBadge } from '../domain/appBadge.js';

interface Page {
  id: string; label: string; hint: string; min?: Role;
  /**
   * Reachable by its address, never a tab (Pass 16 call 1). Your data is every role's page and
   * lives behind every role's door, the profile menu; the Admin section keeps only gated tabs.
   */
  hidden?: boolean;
}
interface Section {
  id: string;
  label: string;
  pages: Page[];
  /**
   * Reachable, but not a tab anywhere (Tim, 22 August).
   *
   * Admin is the only one. It is a destination people go to on purpose from Home, not somewhere
   * to be browsed past — and it was appearing three times over: a top-level tab, a row in the
   * phone's More sheet behind your own face, and now the amber pill beside Overview and My team.
   * The pill is the one Tim asked for, so the other two go and the section stays routable.
   */
  unlisted?: boolean;
  /**
   * The whole section disappears below this rank, tab and all.
   *
   * Distinct from gating every page inside it, which would leave the tab standing with nothing
   * behind it until the empty-section filter caught up. Stating it once on the section is also
   * the honest description of what `Admin` is: not a screen a manager is allowed part of, but a
   * room he has no business in.
   */
  min?: Role;
}

const SEASON_LABEL = '2026';

/**
 * The sections that get their own slot on the phone. Everything else is in the avatar's sheet.
 *
 * History earned one on second thoughts: it is where the league argues — records, champions,
 * every trade ever made — and it was a tap behind a menu while `Admin`, which two people can
 * open at all, sat beside it. Anything not here still reaches its own screen from the sheet, so
 * the list is a question of how often, not of whether.
 */
const BAR = ['home', 'keepers', 'draft', 'league', 'history'];

/**
 * Six sections, one door per task.
 *
 * The old shape had six sections and three destinations you could reach from two places —
 * keeper history under both Keepers and League, past drafts under Draftboard, manager history
 * buried in Settings. A reader who wanted "the record" had to know which of four doors held the
 * bit he wanted.
 *
 * TEAM IS GONE as a section. Its two actions belonged to other sections all along: declaring
 * keepers is a Keepers task and ranking slots is a Draft task, and putting them under "Team"
 * meant the two things with actual deadlines on them lived in the section nobody opened.
 *
 * HISTORY IS NEW, and it is a container rather than a rewrite: it gathers the four record screens
 * that already existed and were scattered. The tables inside it are unchanged for now.
 *
 * Two interim berths, both marked below, both disappearing when the screen that absorbs them is
 * built: My team sits under Home until Home takes over its content, and Review sits under Keepers
 * until My slate absorbs it.
 */
const SECTIONS: Section[] = [
  /*
   * ONE PAGE, SO NO SUB-NAV (Pass 14, Tim's decision B).
   *
   * `My team` is gone. It answered "what have I actually got?" with a roster, the keeper values
   * and a FAAB figure, and Home now answers the parts of that a manager acts on: the picks he
   * holds after keepers, his balance, his farm. The roster itself and the per-player price live
   * inside `Keepers > Declare`, which is where they are read while being used rather than looked
   * at on their own.
   *
   * With one page the contextual second row disappears on its own: `section.pages.length > 1` is
   * what draws it, which is the whole reason the Admin door moved to the commissioner card.
   * `#/home/myteam` still resolves; see `RELOCATED`.
   */
  {
    id: 'home',
    label: 'Home',
    pages: [
      { id: 'home', label: 'Overview', hint: 'What needs doing, and where the league stands' },
    ],
  },
  {
    id: 'keepers',
    label: 'Keepers',
    pages: [
      { id: 'slate', label: 'Declare', hint: 'Declare your 2026 keepers', min: 'manager' },
      /*
        NO DRAFTBOARD TAB. It was the full priced list of every keepable player, and that list is
        now half of `Every slate` on the Declare page — the same reference table, with each GM's
        declaration laid over it, so you read "what could be kept" and "what has been" in one
        place instead of holding one tab in your head while looking at the other.
      */
      { id: 'farm', label: 'Farm', hint: 'Every farm pick on record' },
      { id: 'calc', label: 'Calculator', hint: 'Price a hypothetical' },
      /*
       * KEEPER HISTORY LIVES WITH KEEPERS (Tim, 22 August). It was under History, which grouped it
       * with champions and payouts — records you read once a year — when the question it answers,
       * "what did he cost last time", is asked while you are declaring. `#/history/keepers` still
       * resolves; see `RELOCATED`.
       */
      { id: 'history', label: 'Past keepers', hint: 'Who kept whom, and what it cost' },
      // Everything else in this section is a reference table, and a viewer is welcome in all of
      // it. Only the two screens with the word "my" in them need a team behind them.
    ],
  },
  {
    id: 'draft',
    label: 'Draft',
    pages: [
      /*
       * ONE BOARD, WITH A MOCK TOGGLE ON IT — Tim's call, 2026-08-17.
       *
       * `Live board` and `Mock draft` were two tabs showing the same object, and the difference
       * between them is a mode rather than a destination. Worse, on the night they sat one click
       * apart: a man could enter his real first-round pick into the sandbox and lose it silently.
       * The toggle lives on the board and closes half an hour before the draft.
       */
      { id: 'board', label: 'Draftboard', hint: 'Keepers preloaded, enter your own picks' },
      /*
       * ONE PAGE — Tim's call, 2026-08-17.
       *
       * `My slot` and `Order and lottery` were two tabs over one process, and the overlap was
       * total: both carried the deadline, both carried the three-step explainer, both carried the
       * published order, and neither made sense without the other half. A manager cannot rank six
       * slots without knowing a lottery decided he chooses sixth.
       *
       * The page already knew how to be both — it takes an `only` prop and grows a pane switch
       * when it is absent — so the merge is deleting a door rather than writing a screen. A
       * manager gets his ranking; a commissioner gets that plus the lottery machinery beside it.
       */
      { id: 'order', label: 'Order and lottery', hint: 'Rank your slots, and how the order gets decided' },
      /* Past drafts sits with the draft for the same reason keeper history sits with keepers. */
      { id: 'drafts', label: 'Past drafts', hint: '23 seasons from the archive workbook' },
    ],
  },
  {
    id: 'league',
    /*
     * FOUR PAGES (Tim, 22 August), down from six plus a whole section of its own.
     *
     * `Scoring` and `The Grid` were tabs here AND panes inside the rulebook — two designs for one
     * job, which is the thing this app keeps having to be talked out of. `Proposals` was a
     * separate destination for the process that CHANGES the rules you are looking at, so the two
     * are now tabs of one page. And History came in from the top-level nav, minus the three
     * screens that belong beside the work they describe.
     *
     * Everything removed still resolves — see `RELOCATED` — because those ids are in Discord
     * links, in the rulebook's own citations, and in people's bookmarks.
     */
    label: 'League',
    pages: [
      { id: 'faab', label: 'FAAB', hint: 'Balances and the ledger behind them' },
      { id: 'trades', label: 'Trades', hint: 'Eleven years of deals, searchable by player or pick' },
      { id: 'rules', label: 'Rules', hint: 'Every rule in force' },
      /*
       * PROPOSALS IS A PEER OF RULES, not a view of it (`design_handoff_docket`, 27 August).
       *
       * It was a sub-toggle inside a page called Rules, which meant a row of chrome whose only
       * job was to say `Rules | Proposals` underneath a tab that already said `Rules`. A level
       * that has to repeat its parent's name to distinguish itself is a level that should not
       * exist — and the nesting asserted something untrue besides: a proposal is not a view of
       * the rulebook, it is a candidate to enter it, with its own lifecycle and its own night.
       *
       * `#/league/rules/proposals` still resolves; see the redirect below.
       */
      { id: 'proposals', label: 'Proposals', hint: 'What the committee is being asked to change' },
    ],
  },
  /*
   * HISTORY IS PRIMARY AGAIN (`design_handoff_docket`, 27 August), and the reason it came back is
   * the reason it left: it spans keepers, drafts, trades and rules alike, so filing it inside
   * League made it findable only by somebody who already knew to look there. On the phone it
   * takes the tab-bar slot the avatar was using, and the avatar goes to the header where it
   * already sits on the desktop.
   *
   * The three screens that used to live here stay where they are — past drafts under Draft and
   * keeper history under Keepers, beside the work they describe. Every old `#/history/...` id
   * still resolves; see `RELOCATED`.
   */
  {
    id: 'history',
    label: 'History',
    pages: [
      { id: 'hub', label: 'History', hint: 'Champions, seasons, managers and records' },
    ],
  },
  {
    id: 'settings',
    unlisted: true,
    /*
      ADMIN, NOT SETTINGS, and hidden from everybody else.
      "Settings" reads like preferences — the place you go to change your own colours — and this
      is the opposite of that: deadlines the whole league is bound by, the Grid, the season
      rollover, the player index the board searches. Naming it Admin says who it is for, and the
      `min` below means a manager never sees the tab at all.

      IT TAKES THREE MANAGER-FACING PAGES WITH IT: Deadlines, Player index and Saved data. The
      dates are already on Home and in the phase strip, and the index is reference nobody was
      reaching for here — but `Saved data` is where a manager backs his own work up, and it is
      now behind a door he cannot open. If that bites, it wants to move rather than come back.
    */
    label: 'Admin',
    min: 'commissioner',
    /*
       FOUR PAGES, GROUPED BY WHAT THEY ARE FOR.
       `Settings` is every control: the dates, the clocks, the league values, the Grid, what has
       been set by hand, and ending the season. The player index and saved data are reference.
       Draft tools is the admin's own workbench. `Commissioner tools` used to be a fifth tab
       holding half of Settings, which nobody could predict the contents of from its name.
    */
    pages: [
      {
        id: 'deadlines',
        label: 'Settings',
        hint: 'Dates, locks, the draft order and what the league runs on',
      },
      {
        id: 'access',
        label: 'Access',
        hint: 'Who has signed in, what they may do, and whether the door is open',
        min: 'admin',
      },
      { id: 'index', label: 'Player index', hint: 'The NFL and college pools the board searches' },
      { id: 'saved', label: 'Your data', hint: 'Where the league\u2019s data lives, and how to back it up', hidden: true },
      {
        id: 'drafttools',
        label: 'Draft tools',
        hint: 'ADP, how they draft, and what the next four rounds might look like',
        min: 'commissioner',
      },
      /*
        ADMIN, NOT COMMISSIONER, and it is the only page in the app with that gate for a reason:
        commissioners move FAAB, affirm trades for people and edit slates, so they are who this
        log is most often evidence about. `firestore.rules` refuses them the read as well — this
        line only keeps it off their nav.
      */
      {
        id: 'audit',
        label: 'Activity',
        hint: 'Every change the league has a record of, and who made it',
        min: 'admin',
      },
    ],
  },
];

function Shell() {
  const { session, loading, signOut, viewAs } = useSession();
  const { store, connecting, fellBack } = useLeagueStore();
  // Loaded once for the whole app: the records engine reads the pairings through a module
  // overlay, so every board is consistent no matter which screen you arrive from.
  usePairingLoader(store);
  // The roster feeds GMS, so the shell repaints when a manager is added and every picker below
  // it — sign-in, acting-as, the FAAB ledger, the lottery — picks him up without a reload.
  useRosterLoader(store);
  useRosterVersion();
  /**
   * Where we are, plus anything the previous screen wanted to hand over.
   *
   * `focus` is how one screen asks another to show a particular thing — the keeper history
   * pointing at a pick in a past draft. Carried on the navigation state rather than in a store,
   * because it is not data: it is the reason for this navigation, and it expires with it.
   */
  const [at, setAt] = useState<
    { section: string; page: string; focus?: DraftFocus; ruleId?: string; view?: string }>(
    () => parseHash(typeof window === 'undefined' ? '' : window.location.hash)
      ?? { section: 'home', page: 'home' },
  );

  /*
   * THE HALF OF THE ACTIVITY LOG THAT CANNOT BE DERIVED.
   *
   * Everything else on Admin › Activity is read off the records themselves, which is retroactive
   * and unfalsifiable. A sign-in and a page view leave no record to read, so they are written as
   * they happen — here, above every early return, because a hook that only runs once the session
   * has resolved is a hook that never sees the sign-in it exists to record.
   *
   * `session.realGm` is the ACCOUNT and `session.gm` is who he is acting as; the log wants both,
   * because Tim-viewing-as-Luke opening Keepers is a different event from Luke doing it.
   * `auditor.ts` swallows every failure and coalesces views — see the rules at the top of it.
   */
  /*
   * `realGm ?? gm` is the ACCOUNT; `gm` is who he is currently acting as. An unpaired session
   * carries `gm: ''`, so the sign-in hook falls back to the uid — those are the arrivals an
   * access log most wants and the ones it used to drop on the floor.
   */
  const auditActor = session?.realGm || session?.gm || session?.uid || null;
  useSignInAudit(store, session
    ? {
      gm: session.realGm || session.gm,
      uid: session.uid,
      displayName: session.displayName,
      email: session.email,
      link: session.link,
    }
    : null);
  useActingAsAudit(store, session?.realGm || session?.gm || null, session?.actingAs);
  useViewAudit(store, auditActor, `${at.section}/${at.page}`, session?.gm);

  /*
   * Back and forward.
   *
   * The hash is the record of where you are: navigating writes it, and the browser's own
   * buttons — plus the phone's back gesture — read it. Both directions guard on equality, so a
   * navigation does not bounce between the two effects.
   */
  useEffect(() => {
    const onHash = () => {
      const next = parseHash(window.location.hash);
      if (next) setAt((prev) => (sameWhere(prev, next) ? prev : next));
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    const want = formatHash(at);
    if (window.location.hash !== want) window.location.hash = want;
    // `ruleId` is in the deps: a second citation to a different rule on the page you are already
    // on has to rewrite the hash, or the link does nothing.
    // `view` too: switching tab inside a page is a navigation, and without it in the deps the
    // screen would change while the address bar kept pointing at the tab you just left.
  }, [at.section, at.page, at.ruleId, at.view]);
  /** The mobile More sheet (2a). Desktop never opens it — the bar is display:none there. */
  const [more, setMore] = useState(false);
  /* The desktop header's own version of that sheet — see `ProfileMenu`. */
  const [me, setMe] = useState(false);
  const chrome = useChrome(store, session?.gm ?? '');
  /*
   * WHO IS WAITING TO GET IN, for the dot on your own avatar.
   *
   * Deliberately not part of `useChrome`. That is built from store subscriptions every signed-in
   * manager has; this is one HTTP call to `/api/links` that only an admin is allowed to make, and
   * for anybody else the hook returns an empty queue without sending anything.
   *
   * It hangs off the AVATAR rather than off the Admin section, because the thing being reported is
   * not "there is work in that menu" — it is "somebody is waiting on YOU". The commissioner cannot
   * act on it and does not get it.
   */
  const access = useAccessRequests(session?.realRole ?? session?.role ?? 'viewer');

  /*
   * THE NUMBER ON THE HOME-SCREEN ICON (Tim, 26 August).
   *
   * Deliberately a much smaller set than the nav badges beside it: a deadline inside the week
   * before it falls due, plus an access request for somebody who can answer one. See
   * `domain/appBadge.ts` for why the icon has to be stingier than the nav, and for the honest
   * limit — this is set when the app is OPENED and then sits on the icon afterwards. It is a
   * reminder of what you last saw, not a live counter; a deadline crossing into its final week
   * while the app is shut will not light it up until the app is next opened. Making it live needs
   * a push server, which does not exist.
   */
  useEffect(() => {
    void showAppBadge(appBadgeCount({
      badges: chrome.badges,
      dueBySection: chrome.badgeDue,
      accessRequests: access.waiting,
      canAdmit: atLeast(session?.realRole ?? session?.role ?? 'viewer', 'admin'),
    }));
  }, [chrome.badges, chrome.badgeDue, access.waiting, session?.realRole, session?.role]);

  /*
   * The height of the sticky chrome, published as `--chromeh`.
   *
   * Anything else that wants to pin itself under the header has to know where the header ends, and
   * the header's height is not a constant: it carries the live-draft strip when a draft is
   * running, the sub-tab pills wrap at narrow widths, and the phase strip comes and goes. The farm
   * auction's bar was pinned at a guessed 56px and landed in the middle of it.
   */
  /*
    A CALLBACK REF, not a `useEffect` over `chromeRef.current`. The effect version ran exactly
    once — on the first render, which is the SIGN-IN screen, where `.chrome` does not exist. It
    found null, bailed, and with an empty dependency list never ran again, so `--chromeh` was
    never published and everything pinned to it sat at the 56px fallback inside a 134px header.
    A callback ref fires when the node attaches, whenever that turns out to be.
  */
  const observing = useRef<{ el: HTMLDivElement; ro: ResizeObserver | null } | null>(null);
  const chromeRef = useCallback((el: HTMLDivElement | null) => {
    if (observing.current) {
      observing.current.ro?.disconnect();
      observing.current = null;
    }
    if (!el) return;
    const publish = () => document.documentElement.style
      .setProperty('--chromeh', `${Math.round(el.getBoundingClientRect().height)}px`);
    publish();
    // Not available in every browser the league uses; the initial measurement still stands.
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    ro?.observe(el);
    observing.current = { el, ro };
  }, []);
  /* The rule panel's "Governs" links and its primary action send you to the screen that
     enforces the rule. Navigation lives here, so the panel is handed a way to reach it. */
  useEffect(() => {
    setRuleNavigator((s2, p2, ruleId) => setAt({ section: s2, page: p2, ruleId }));
    return () => setRuleNavigator(null);
  }, []);

  /*
   * The favicon, set from the bundle rather than linked. Both builds get it — including the
   * standalone opened from file://, where a linked icon file would not resolve.
   */
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
      ?? document.head.appendChild(Object.assign(document.createElement('link'), { rel: 'icon' }));
    link.type = 'image/png';
    link.href = FAVICON_URL;
  }, []);

  /*
   * A PAGE IS OPEN TO EVERYBODY UNLESS IT SAYS OTHERWISE.
   *
   * The default used to be `manager`, which was harmless while `manager` was the bottom of the
   * ladder and wrong the moment a viewer existed — Neel would have signed in to an app with no
   * tabs at all. The three screens that need a team behind them say so; every other screen is a
   * board, a table or the rulebook, and there is nothing to protect on it.
   */
  const sections = useMemo(() => SECTIONS
    .filter((s) => atLeast(session?.role, s.min ?? 'viewer'))
    .map((s) => ({ ...s, pages: s.pages.filter((p) => atLeast(session?.role, p.min ?? 'viewer')) }))
    .filter((s) => s.pages.length > 0), [session?.role]);

  /*
   * WHOLE SCREENS THAT CHANGED SECTION, applied before anything else looks at `at`.
   *
   * `MOVED` below only redirects a page within its own section, which was enough until the League
   * restructure (Tim, 22 August) moved four screens across sections and deleted a fifth. These ids
   * are in Discord links, in bookmarks, and in the twenty `§` citations the rulebook page just
   * went live with — landing any of them on "the section's first tab" would be a wrong answer
   * delivered confidently, which is worse than a 404.
   *
   * The trade archive redirects to League › Trades rather than to the History page, because that
   * is where its content actually went: the two were the same object and only one of them was
   * being maintained.
   */
  const RELOCATED: Record<string, { section: string; page: string; view?: string }> = {
    /* My team, deleted in Pass 14. Its content is inside the declare screen. */
    'home/myteam': { section: 'keepers', page: 'slate' },
    'history/champions': { section: 'history', page: 'hub' },
    'history/seasons': { section: 'history', page: 'hub' },
    'history/managers': { section: 'history', page: 'hub' },
    'history/records': { section: 'history', page: 'hub' },
    'history/drafts': { section: 'draft', page: 'drafts' },
    'history/keepers': { section: 'keepers', page: 'history' },
    'history/trades': { section: 'league', page: 'trades' },
    /* The berth History kept for five days, while it was a page under League. */
    'league/history': { section: 'history', page: 'hub' },
    'league/scoring': { section: 'league', page: 'rules' },
    'league/grid': { section: 'league', page: 'rules' },
  };
  /*
   * `#/league/rules/proposals` — the address the sub-toggle used to own.
   *
   * It is in Discord links and in the committee strip's own `onGo` from a week ago, and it is
   * the one relocation `RELOCATED` cannot express, because the thing that moved is a third
   * segment rather than a page id. Same bridge, one line lower.
   */
  const fromToggle = at.section === 'league' && at.page === 'rules' && at.view === 'proposals';
  const here = fromToggle
    ? { ...at, page: 'proposals', view: undefined }
    : RELOCATED[`${at.section}/${at.page}`] ?? at;
  /*
   * AND THE URL CATCHES UP. Without this the old id keeps showing in the address bar while the new
   * screen is on it, so anybody who copies the link out is handing on a dead id — the redirect
   * quietly becomes load-bearing forever instead of being a bridge. Guarded on the lookup having
   * hit, so this never fires for an ordinary navigation.
   */
  useEffect(() => {
    if (fromToggle) {
      setAt((prev) => ({ ...prev, page: 'proposals', view: undefined }));
      return;
    }
    const moved = RELOCATED[`${at.section}/${at.page}`];
    if (moved) {
      setAt((prev) => ({
        ...prev, section: moved.section, page: moved.page,
        ...(moved.view ? { view: moved.view } : {}),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [at.section, at.page, at.view]);

  const section = sections.find((s) => s.id === here.section) ?? sections[0]!;
  /*
   * `mock` is a destination without a tab.
   *
   * Folding the mock into the Draftboard took it out of `SECTIONS`, which is right — it is a mode
   * of that screen, not a fifth place to go. But the shell resolves a page by looking it up in the
   * section's tab list, so removing the tab also broke the route: the toggle called for `mock`,
   * the lookup found nothing, and it fell back to the board. The sub-nav still shows three tabs;
   * this one destination is simply reachable without being one of them.
   *
   * It also keeps `/#/draft/mock` working for anybody who bookmarked it or pasted it in the chat.
   */
  const UNLISTED: Record<string, Page> = {
    mock: { id: 'mock', label: 'Mock draft', hint: 'Play it out privately — nothing is saved' },
  };
  /*
   * Pages that used to exist and now live inside another one. A bookmark, a Discord link or a
   * rulebook cross-reference should land on the screen that absorbed it rather than silently on
   * the section's first tab, which is a different subject entirely.
   */
  const MOVED: Record<string, Record<string, string>> = {
    draft: { slot: 'order' },   // My slot folded into Order and lottery, 2026-08-17
  };
  const wanted = MOVED[section.id]?.[here.page] ?? here.page;
  const page = section.pages.find((p) => p.id === wanted)
    ?? (section.id === 'draft' ? UNLISTED[wanted] : undefined)
    ?? section.pages[0]!;

  const go = (sectionId: string) => {
    const s = sections.find((x) => x.id === sectionId) ?? sections[0]!;
    setAt({ section: s.id, page: s.pages[0]!.id });
  };

  /**
   * NAVIGATION IS A LINK, AND THE BROWSER GETS FIRST REFUSAL ON IT.
   *
   * The nav was `<button onClick>`. Every destination in this app already HAS a URL — the router
   * is the hash and `formatHash` writes it — but a button is not a link, so right-clicking a tab
   * offered Back / Forward / Reload and nothing else. Tim, 31 August: "i don't get 'open in a new
   * tab' when I right-click the link." Cmd-click and middle-click were gone for the same reason,
   * and a middle-click that does nothing is the more annoying of the two.
   *
   * So the tabs are anchors with a real `href`, and this handler takes the plain left click ONLY.
   * Anything the browser has its own meaning for — another mouse button, a modifier, a right-
   * click — falls straight through, which is how you get the new tab, the new window, the
   * bookmark and the copied address for free.
   *
   * `preventDefault` on the plain click rather than letting the hash change do the work: `at`
   * carries `focus` and `ruleId` that the URL does not, and a round trip through `hashchange`
   * would drop them.
   */
  const navTo = (e: React.MouseEvent, run: () => void) => {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    run();
  };

  if (loading) {
    return <div className="viz-root shell"><p className="boot">Checking your session…</p></div>;
  }
  if (!session?.gm) return <SignIn />;
  /**
   * Held here deliberately rather than rendering the app against the memory store and swapping
   * underneath it. The swap version works right up until somebody types during the half-second it
   * takes to connect, at which point their first edit goes into a store nobody else will ever
   * read.
   */
  if (connecting) {
    return <div className="viz-root shell"><p className="boot">Opening the league…</p></div>;
  }

  const gm = session.gm;
  const actor = session.realGm ?? session.gm;
  const role = session.role;
  const jump = (s: string, p: string, focus?: DraftFocus) => setAt({ section: s, page: p, focus });

  /*
   * The live strip (10a/10b) is for people who have LEFT the board. On the draft screens it would
   * be a bar telling you to go back to the page you are on, three times smaller than the
   * on-the-clock card already in front of you.
   */
  const stripUp = chrome.strip.state !== 'off' && section.id !== 'draft';

  /*
   * THE COMMITTEE STRIP FOLLOWS THE SAME RULE, and yields to the draft's.
   *
   * Same reasoning as above: it is for people who have LEFT the meeting, so it does not appear on
   * League › Rules, where the room itself is. And two live bars stacked above the nav is two
   * things shouting on the one night a year they could overlap — the draft's is the louder event
   * and it wins, which costs nothing, since the committee meets in August and the draft is one
   * evening in September.
   */
  /*
   * THE LOTTERY STRIP SITS BETWEEN THEM in the same one-bar-at-a-time queue.
   *
   * It yields to the draft's for the reason the committee's does, and it outranks the committee's
   * on the one night they overlap — the draw opens the meeting, it lasts under a minute, and it
   * is the half of that evening you cannot catch up on afterwards. A committee strip is still
   * right thirty seconds later; a lottery strip is not.
   *
   * Not on the page it points at, and not on the reveal itself: `Draft › Order` swaps to the
   * full-page board the moment a draw is recorded, and a bar above it saying "watch the draw"
   * would be pointing at the thing filling the screen.
   */
  const lotteryUp = chrome.lottery.live && !stripUp
    && !(section.id === 'draft' && page.id === 'order');

  const committeeUp = chrome.committee.live && !stripUp && !lotteryUp
    && !(section.id === 'league' && page.id === 'proposals');

  return (
    <div className={`viz-root shell ${stripUp || lotteryUp || committeeUp ? 'striplive' : ''}`}>
      <div className="chrome" ref={chromeRef}>
      {stripUp && (
        <DraftStrip info={chrome.strip} onGo={() => setAt({ section: 'draft', page: 'board' })} />
      )}
      {lotteryUp && (
        <LotteryStrip info={chrome.lottery}
          onGo={() => setAt({ section: 'draft', page: 'order' })} />
      )}
      {committeeUp && (
        <CommitteeStrip info={chrome.committee}
          onGo={() => setAt({ section: 'league', page: 'proposals' })} />
      )}
      <nav className="topnav">
        <a className="brand" href={formatHash({ section: 'home', page: 'home' })}
          onClick={(e) => navTo(e, () => go('home'))}>
          {/* The league crest, from Tim's artwork, inlined so the offline file has it too. */}
          <img className="crest" src={LOGO_URL} alt="" width={26} height={26} />
          <span className="word">League Manager</span>
          <span className="season">{SEASON_LABEL}</span>
        </a>
        <div className="tabs" role="tablist" aria-label="Sections">
          {sections.filter((s) => !s.unlisted).map((s) => (
            <a
              key={s.id}
              role="tab"
              aria-selected={section.id === s.id}
              className={`tab ${section.id === s.id ? 'on' : ''}`}
              href={formatHash({ section: s.id, page: s.pages[0]!.id })}
              onClick={(e) => navTo(e, () => go(s.id))}
            >
              {s.label}
              {/*
                While the draft is live the Draft item carries a pill — accent `LIVE`, amber `YOU`
                when the pick on the clock is yours (10a). It replaces the to-do badge rather than
                sitting beside it: a section cannot be both "you owe this" and "you are up".
              */}
              {/*
                THE SAME PILL FOR THE OTHER LIVE EVENT (handoff §3). Green rather than accent,
                because it is the room's colour and because the two states are genuinely different
                things happening — a reader who sees both in one year should not have to work out
                which section is which.
              */}
              {s.id === 'draft' && chrome.strip.state !== 'off' ? (
                <span className={`livepill ${chrome.strip.state === 'yours' ? 'yours' : ''}`}>
                  <i aria-hidden="true" />
                  {chrome.strip.state === 'yours' ? 'YOU' : 'LIVE'}
                </span>
              ) : s.id === 'league' && chrome.committee.live ? (
                <span className="livepill committee">
                  <i aria-hidden="true" />
                  LIVE
                </span>
              ) : (
                /*
                  A DOT, NOT A COUNT.
                  The number was answering a question nobody asks of a nav bar: "two what?" is
                  not something you can act on, and the figure widened the item enough to push
                  the whole menu around as work appeared and cleared. What the bar has to say is
                  "there is something for you in here" — one bit — and the section you land in
                  says the rest. The count survives on the tooltip for anyone who wants it.

                  Absolutely positioned so it hangs off the corner rather than sitting in the
                  line: a dot in the text flow moves the label, which is the thing being read.
                */
                chrome.badges[s.id] && (
                  <span className="navdot"
                    role="img"
                    aria-label={chrome.badgeWhy[s.id] ?? `${chrome.badges[s.id]} to do`}
                    title={chrome.badgeWhy[s.id] ?? `${chrome.badges[s.id]} to do`} />
                )
              )}
            </a>
          ))}
        </div>
        {/*
          The season's progression, moved out of its own row and into the top bar on Tim's call.
          It is a status, not a destination, and it was costing a full band of chrome above every
          screen — which the farm auction's pinned bar then had to measure around. The chevrons
          and their reasoning are unchanged; only where they live is.
        */}
          {/*
            THE SEASON, AND THE ONE FIGURE BESIDE IT (Pass 14).

            Both were written inline here with a distance ramp, per-end padding compensation and a
            1340px fallback; both are components now, and the reasoning for each is on them. What
            changed on screen: four flat segments with a page-ground seam instead of five tinted
            ones that overlapped, and a countdown that names which of four things it knows rather
            than always counting to a draft, sometimes to a guess it did not mention.
          */}
          <PhaseFlow phase={chrome.phase} />
          <Countdown info={{
            phase: chrome.phase,
            days: chrome.days,
            announced: !chrome.draft.projected,
            /*
              The week is derived from the ANNOUNCED date only. A projected draft would produce a
              projected week, printed as a bare number with nothing to say it was invented.
            */
            week: seasonWeek(chrome.draft.projected ? null : chrome.draft.iso, Date.now()),
          }} />
        {/*
          ONE IDENTITY PANEL, TWO SHAPES (Tim, 21 August).

          This was three flat controls — an `acting as` select, the role chip and Sign out — while
          the phone had already settled on your face opening a single panel that holds all of it.
          Two designs for one job, and the phone's was the better one. The desktop now opens the
          same `IdentityPanel` in a popover, so there is one component to change and one thing for
          a manager to learn on either device.
        */}
        <div className="who">
          {/*
            THE PHONE'S FACE, in the header. It opens the same sheet the tab bar's fifth slot used
            to — not the desktop's popover, which is a 260px card and the wrong object at 390px.
            One button, shown by CSS under 760 and never both at once, so there is exactly one
            place your own face is on any screen.
          */}
          <button type="button" className={`mefacebtn ${more ? 'on' : ''}`}
            aria-expanded={more} aria-label="You, and the rest of the app"
            onClick={() => setMore((v) => !v)}>
            <GmBadge gm={session.actingAs ?? session.gm} size="sm" />
            {/*
              WAITING BEATS BORROWED — the same rule the tab bar's dot followed, and for the same
              reason: they are one dot in one corner and cannot both be drawn.
            */}
            {access.waiting > 0 ? (
              <span className="medot waiting" role="img"
                aria-label={`${access.waiting} waiting to be let in`}
                title={`${access.waiting} waiting to be let in`} />
            ) : session.actingAs && <span className="medot borrowed" aria-hidden="true" />}
          </button>
          <ProfileMenu
            gm={session.actingAs ?? session.gm}
            title={session.actingAs
              ? `${ROLE_LABEL[role]} — you are ${managerShort(actor)}`
              : ROLE_LABEL[role]}
            open={me}
            onOpen={setMe}
            starred={role !== 'manager'}
            borrowed={!!session.actingAs}
            waiting={access.waiting}
          >
            {(close) => (
              <IdentityPanel
                gm={session.actingAs ?? session.gm}
                actor={actor}
                roleLabel={ROLE_LABEL[role]}
                actAs={atLeast(session.realRole ?? role, 'admin') ? GMS : []}
                actingAs={session.actingAs ?? null}
                onActAs={(g) => viewAs(g)}
                onSignOut={() => { close(); void signOut(); }}
                store={store}
                canEditAvatar={!session.actingAs || atLeast(session.realRole ?? role, 'commissioner')}
                onYourData={() => { close(); jump('settings', 'saved'); }}
                {...(atLeast(role, 'commissioner')
                  ? { onAdmin: () => { close(); jump('settings', 'deadlines'); }, waiting: access.waiting }
                  : {})}
              />
            )}
          </ProfileMenu>
        </div>
      </nav>

      {/*
        Where the season is (1b).

        Its job is unchanged and still load-bearing: Home's content shifts between phases, and
        stating where the season is makes that read as intentional rather than as things
        vanishing.
      */}

      {section.pages.filter((p) => !p.hidden).length > 1 && (
        <div className="subnav">
          {/*
            WHERE YOU ARE, BEFORE WHICH PAGE (CD, 6 September). The row named five pages and
            nothing named the section they belong to, so the sub-nav read as a second top-level
            menu — five words with no parent. The section leads it, in the accent, with a rule
            of its own; the pages follow in ink. One prefix, every section.

            IT IS A SIBLING OF THE SCROLLER, NOT A CHILD OF IT (Tim, 7 September). Sticky held it
            still in Chromium and not in WebKit; outside the scrolling box it cannot move at all.
            The tablist moves with the tabs so the role still wraps exactly them.
          */}
          <span className="subwhere" aria-hidden="true">
            <i />
            {section.label}
            <b>/</b>
          </span>
          <div className="subtabs" role="tablist" aria-label={`${section.label} views`}>
          {section.pages.filter((p) => !p.hidden).map((p) => (
            <a
              key={p.id}
              role="tab"
              aria-selected={page.id === p.id}
              className={`subtab ${page.id === p.id ? 'on' : ''}`}
              title={p.hint}
              href={formatHash({ section: section.id, page: p.id })}
              onClick={(e) => navTo(e, () => setAt({ section: section.id, page: p.id }))}
            >
              {p.label}
              {/*
                THE SUB-TAB SAYS WHERE THE COUNT CAME FROM.
                A `1` on the section means something is owed inside it; without this the reader
                has to open all three pages to find out which. The dot is the same fact one level
                down, and it disappears the moment the thing is done because both read the same
                source.
              */}
              {chrome.badgePage[section.id] === p.id && (
                <span className="owedot" title={chrome.badgeWhy[section.id]} aria-label="needs you" />
              )}
              {/*
                ACCESS CARRIES ITS OWN DOT (Tim, 22 August).

                Every other badge on this row comes from `useChrome`, which is built from store
                subscriptions every signed-in manager has. The access queue is not: it is one HTTP
                call to `/api/links` that only an admin may make, so it never reached `chrome` and
                the sub-tab was the one place with work behind it and nothing saying so. The dot on
                the avatar said "somebody is waiting on you"; this says which tab.
              */}
              {section.id === 'settings' && p.id === 'access' && access.waiting > 0 && (
                <span className="owedot" role="img"
                  aria-label={`${access.waiting} waiting to be let in`}
                  title={`${access.waiting} waiting to be let in`} />
              )}
              {/* 11a: the Live board sub-tab carries the same pill the Draft item does. */}
              {section.id === 'draft' && p.id === 'board' && chrome.strip.state !== 'off' && (
                <span className={`livepill ${chrome.strip.state === 'yours' ? 'yours' : ''}`}>
                  <i aria-hidden="true" />
                  {chrome.strip.state === 'yours' ? 'YOU' : 'LIVE'}
                </span>
              )}
            </a>
          ))}
          </div>
          {/*
            THE ADMIN DOOR IS NOT HERE ANY MORE (Pass 14, Tim's decision A).

            It was a tab on this row, which only existed because Home had a sub-nav for it to sit
            in. Home has one page now, so the row is not drawn on Home at all, and the door moved
            to the pinned foot row of the commissioner card -- the card that is already about
            running the league, renders in every phase, and is invisible to a manager.

            It did NOT go back into the identity sheet. That was tried in August and reversed: the
            sheet is where an app puts things about YOU, and running the league is not that.
          */}
          {/* No hint text in the row: the drawing puts a page's purpose in its subtitle, and a
              right-aligned sentence here is what stopped the sub-nav reading as tabs. */}
        </div>
      )}
      </div>

      {fellBack && (
        <div className="impersonating bad">
          <span className="ico" aria-hidden="true">▲</span>
          <span>
            <b>The league database could not be reached</b>, so the app is running on this browser
            alone — nothing you enter here will reach anybody else, and nobody else&rsquo;s work
            will reach you. Back up from Admin → Saved data before you do much, and reload once
            you have a connection. <span className="mut">({fellBack})</span>
          </span>
        </div>
      )}

      {/*
        THE VIEWER SAYS SO ONCE, AT THE TOP, AND THEN GETS OUT OF THE WAY.
        Neel is signed in to watch, and the failure mode to avoid is not him breaking something —
        he cannot — it is him tapping a control that quietly does nothing and concluding the app
        is broken. One line naming the state beats a greyed button on every screen explaining
        itself. He keeps his own history: he managed a team from 2013 to 2017 and it is still in
        the record under his name.
      */}
      {role === 'viewer' && (
        <div className="impersonating viewing">
          <span className="ico" aria-hidden="true">◉</span>
          <span>
            You are watching. Every board, table and rule is here to read — <b>Ask the Record</b>,
            the draft board live on the night, the farm auction as it runs. Nothing on any of them
            is yours to enter, and no screen here acts for anybody else.
          </span>
        </div>
      )}

      {session.actingAs && (
        <div className="impersonating">
          <span className="ico" aria-hidden="true">▲</span>
          <span>
            You are <b>{managerShort(session.realGm ?? '')}</b>, acting as{' '}
            <b>{managerShort(session.actingAs)}</b> — with{' '}
            <b>{ROLE_LABEL[role].toLowerCase()}</b> access only, so you see and can do exactly
            what they can. Anything you save is recorded against your name, not theirs.
          </span>
          <button type="button" className="linkbtn" onClick={() => viewAs(null)}>
            back to {managerShort(session.realGm ?? '')}
          </button>
        </div>
      )}

      <main>
        {section.id === 'home' && page.id === 'home' && (
          <Home gm={gm} role={role} store={store} onGo={jump} chrome={chrome} />
        )}
        {section.id === 'keepers' && page.id === 'slate' && (
          <DeclareKeepers gm={gm} actor={actor} role={role} store={store} />
        )}
        {section.id === 'keepers' && page.id === 'farm' && <FarmLog />}
        {section.id === 'keepers' && page.id === 'calc' && (
          <KeeperCalculator gm={gm} actor={actor} role={role} store={store} />
        )}

        {section.id === 'draft' && page.id === 'board' && (
          <DraftBoard gm={gm} actor={actor} role={role} store={store}
            onGoMock={() => setAt({ section: 'draft', page: 'mock' })} />
        )}

        {section.id === 'settings' && page.id === 'access' && (
          <AccessRequests gm={actor} role={role} store={store} />
        )}
        {section.id === 'draft' && page.id === 'order' && (
          <DraftPosition gm={gm} role={role} store={store} />
        )}
        {/*
          `/#/draft/mock` still resolves, so a bookmark or a link somebody pasted in the group
          chat last week keeps working — it just lands on the board with the mock turned on.
        */}
        {section.id === 'draft' && page.id === 'mock' && (
          <MockDraft gm={gm} store={store}
            onGoLive={() => setAt({ section: 'draft', page: 'board' })} />
        )}

        {section.id === 'league' && page.id === 'rules' && (
          /*
            THE SUB-TOGGLE IS GONE. Rules and Proposals are League tabs now, so the switch that
            used to sit between them is one level of chrome the page no longer draws — see the
            note beside the section list. `byyear` survives as an ORDERING, which is all it ever
            was: `#/league/rules/byyear` lands here with the year ordering already chosen.
          */
          <Rules gm={gm} role={role} store={store} focusRule={at.ruleId}
            order={at.view === 'byyear' ? 'year' : 'topic'} />
        )}
        {section.id === 'league' && page.id === 'proposals' && (
          <Proposals gm={gm} actor={actor} role={role} store={store} />
        )}
        {section.id === 'history' && (
          <HistoryHub gm={gm} actor={actor} role={role} store={store} onGo={jump} />
        )}
        {section.id === 'league' && (page.id === 'faab' || page.id === 'trades') && (
          <League view={page.id as 'faab' | 'trades'}
            gm={gm} actor={actor} role={role} store={store} />
        )}

        {section.id === 'keepers' && page.id === 'history' && <HistoryKeepers />}
        {section.id === 'draft' && page.id === 'drafts' && <DraftHistory focus={at.focus} />}

        {section.id === 'settings' && page.id === 'deadlines' && (
          <>
            {/*
              ONE PAGE, NOT TWO. `Commissioner tools` used to hold the league values, the Grid,
              the override log and the season rollover, on a tab of its own — a split only the
              person who drew it could hold, since setting a deadline and setting the maximum
              keepers are the same act. Both components render here, in the order somebody works
              through them: the dates, then the values, then what has been set by hand, then the
              once-a-year button at the bottom.
            */}
            <AdminSettings gm={gm} role={role} store={store} />
            <CommissionerTools gm={gm} role={role} store={store} />
          </>
        )}
        {section.id === 'settings' && page.id === 'audit' && (
          <AdminAudit gm={gm} role={role} store={store} />
        )}
        {section.id === 'settings' && page.id === 'index' && <Indexes role={role} store={store} />}
        {section.id === 'settings' && page.id === 'saved' && (
          <SavedData role={role} store={store} />
        )}
        {section.id === 'settings' && page.id === 'drafttools' && (
          <DraftTools role={session?.role ?? 'manager'} store={store} />
        )}
      </main>

      {/*
        Mobile navigation, per turn 2a — chosen over the wrapped grid because the grid costs
        235px of chrome before any content and this costs 117px, on every screen, including the
        one where the board wants the whole viewport. Home, Keepers, Draft and League are the
        four touched during keeper season and draft night; History and Settings sit behind More,
        which the design accepts as one extra tap. Rendered always, shown by CSS under 760px.
      */}
      {/*
        FIVE REAL DESTINATIONS (`design_handoff_docket`, 27 August).

        The fifth slot used to be your own face, and it was carrying two jobs: the identity
        controls, and the door to whatever sections the bar had no room for. History coming back
        to the primary menu takes the slot, and the face goes to the phone HEADER — where it
        already is on the desktop, and where a phone user looks for it. The sheet it opens is
        unchanged, and so is everything in it.
      */}
      <nav className="tabbar" aria-label="Sections">
        {BAR.map((id) => {
          const s = sections.find((x) => x.id === id);
          if (!s) return null;
          return (
            <button key={id} type="button"
              className={`bartab ${section.id === id && !more ? 'on' : ''}`}
              aria-current={section.id === id ? 'page' : undefined}
              onClick={() => { setMore(false); go(id); }}>
              <span className="baricon"><NavIcon name={id as NavIconName} /></span>
              <span className="barlabel">{s.label}</span>
              {/*
                A dot, not a number: the phone states that there is work, not how much. During a
                live draft the Draft tab's dot takes the strip's own two colours (10b), "so the
                state is legible even when the strip is scrolled behind a sheet".
              */}
              {id === 'draft' && chrome.strip.state !== 'off' ? (
                <span className={`bardot live ${chrome.strip.state === 'yours' ? 'yours' : ''}`}
                  aria-hidden="true" />
              ) : (
                chrome.badges[id] && <span className="bardot" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </nav>

      {more && (
        <MoreSheet
          items={sections.filter((s) => !BAR.includes(s.id) && !s.unlisted).map((s) => ({
            id: s.id, label: s.label, detail: s.pages.map((p) => p.label).join(', '),
          }))}
          onPick={(id) => { setMore(false); go(id); }}
          onClose={() => setMore(false)}
        >
          <IdentityPanel
            gm={session.actingAs ?? session.gm}
            actor={actor}
            roleLabel={ROLE_LABEL[role]}
            actAs={atLeast(session.realRole ?? role, 'admin') ? GMS : []}
            actingAs={session.actingAs ?? null}
            onActAs={(g) => viewAs(g)}
            onSignOut={() => { setMore(false); void signOut(); }}
            store={store}
            onYourData={() => { setMore(false); jump('settings', 'saved'); }}
            {...(atLeast(role, 'commissioner')
              ? {
                onAdmin: () => { setMore(false); jump('settings', 'deadlines'); },
                waiting: access.waiting,
              }
              : {})}
            /*
              A manager sets his own picture; a commissioner sets anybody's, for the man who
              cannot work an upload on a phone. Same split the rules file enforces, so the button
              is never offered where the write would be refused.
            */
            canEditAvatar={!session.actingAs || atLeast(session.realRole ?? role, 'commissioner')}
          />
        </MoreSheet>
      )}
    </div>
  );
}

/** `AvatarProvider` needs the store, which only exists inside `LeagueStoreProvider`. */
function Faces({ children }: { children: ReactNode }) {
  return <AvatarProvider store={useLeagueStore().store}>{children}</AvatarProvider>;
}

export function App() {
  return (
    <SessionProvider>
      {/* Inside the session, because which store to use depends on whether anybody is signed in. */}
      <LeagueStoreProvider>
        {/* Faces, once, for every `GmBadge` on every screen — see `avatars.tsx`. */}
        <Faces>
          {/* One modal for every citation in the app, so a rule opens the same way from anywhere. */}
          <RuleProvider>
            <Shell />
            {/*
              MOUNTED HERE, not inside `Shell`, and that placement is the feature.

              `Shell` returns `<SignIn/>` early when nobody is signed in, so anything inside it is
              torn down and rebuilt the moment a manager signs in — and the offer would either be
              missed on the login screen or shown twice. One mount outside that branch shows it on
              the Yahoo screen and keeps the same state through the transition, which is what Tim
              asked for. It decides for itself whether to appear at all.
            */}
            <AddToHome />
            {/* One listener for every `title` in the app, so a phone can reach what a mouse hovers. */}
            <TouchHints />
            <PullToRefresh />
          </RuleProvider>
        </Faces>
      </LeagueStoreProvider>
    </SessionProvider>
  );
}
